import { useState, useCallback, useMemo } from 'react';
import { CalculationFormula } from '@/types/calculation-formulas';
import { getFormulaFallbackDisplayName, getPreferredFormulaFallback } from '../formula-utils';

interface UseFormulaWarningModalParams {
  formData: { formulaId?: string };
  allFormulas: CalculationFormula[];
  defaultFormulaId: string | null;
  viableFormulas: CalculationFormula[];
}

/**
 * Hook to manage the formula warning modal state
 * Note: Actual validation logic is in HealthMeasurementsStep component
 */
export const useFormulaWarningModal = ({
  formData,
  allFormulas,
  defaultFormulaId,
  viableFormulas,
}: UseFormulaWarningModalParams) => {
  const [showFormulaValidationModal, setShowFormulaValidationModal] = useState(false);
  const [nonViableFormulaName, setNonViableFormulaName] = useState('');

  const handleNonViableFormulaSelected = useCallback((selectedFormulaId: string, selectedFormulaName: string) => {
    setNonViableFormulaName(selectedFormulaName);
    setShowFormulaValidationModal(true);
  }, []);

  const handleFormulaValidationConfirm = useCallback(() => {
    setShowFormulaValidationModal(false);
    setNonViableFormulaName('');
  }, []);

  const currentFormulaDisplayName = useMemo(() => {
    if (formData.formulaId) {
      const currentFormula = allFormulas.find(f => String(f.id) === String(formData.formulaId));
      if (currentFormula) {
        return getFormulaFallbackDisplayName(currentFormula);
      }
    }
    if (defaultFormulaId) {
      const defaultFormula = allFormulas.find(f => f.id === defaultFormulaId);
      if (defaultFormula) {
        return getFormulaFallbackDisplayName(defaultFormula);
      }
    }
    if (viableFormulas.length > 0) {
      return getFormulaFallbackDisplayName(getPreferredFormulaFallback(viableFormulas, allFormulas));
    }
    return 'a recommended formula';
  }, [formData.formulaId, allFormulas, defaultFormulaId, viableFormulas]);

  const setShowModal = useCallback((open: boolean) => {
    setShowFormulaValidationModal(open);
    if (!open) {
      setNonViableFormulaName('');
    }
  }, []);

  const reset = useCallback(() => {
    setShowFormulaValidationModal(false);
    setNonViableFormulaName('');
  }, []);

  return {
    showFormulaValidationModal,
    nonViableFormulaName,
    currentFormulaDisplayName,
    handleNonViableFormulaSelected,
    handleFormulaValidationConfirm,
    setShowModal,
    reset,
  };
};
